import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ParkMap from "@/components/ParkMap";
import FacilityIcon from "@/components/FacilityIcon";
import { api, formatGBP, FACILITY_LABELS } from "@/lib/api";
import { Star, MapPin, ExternalLink, Waves, ArrowLeft } from "lucide-react";
import { useCompare } from "@/lib/CompareContext";

export default function ParkDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [park, setPark] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const { toggle, has, items } = useCompare();

  useEffect(() => {
    setPark(null);
    const q = Object.fromEntries(params.entries());
    api.getPark(slug, q).then(setPark).catch(() => setPark("notfound"));
  }, [slug, params]);

  if (park === "notfound") return (
    <div className="App"><Nav /><div className="max-w-3xl mx-auto py-32 text-center"><h2 className="font-display text-2xl">Park not found</h2><Link to="/search" className="btn-primary mt-6">Back to search</Link></div></div>
  );
  if (!park) return (
    <div className="App"><Nav /><div className="max-w-5xl mx-auto px-4 py-12 space-y-4"><div className="h-8 w-1/2 rounded skeleton-shimmer" /><div className="aspect-[16/9] rounded-xl skeleton-shimmer" /><div className="h-4 w-3/4 rounded skeleton-shimmer" /></div></div>
  );

  const isCompared = has(park.slug);
  const compareDisabled = !isCompared && items.length >= 3;

  return (
    <div className="App">
      <Nav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/search" className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-forest" data-testid="back-to-search">
          <ArrowLeft className="w-4 h-4" /> Back to search
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[420px]" data-testid="park-gallery">
          <button onClick={() => setLightboxIdx(0)} className="col-span-2 row-span-2 rounded-l-2xl overflow-hidden bg-sand">
            <img src={park.gallery[0]} alt={park.name} className="w-full h-full object-cover hover:scale-105 transition-transform" />
          </button>
          {park.gallery.slice(1, 5).map((g, i) => (
            <button key={i} onClick={() => setLightboxIdx(i+1)} className={`overflow-hidden bg-sand ${i === 1 ? "rounded-tr-2xl" : ""} ${i === 3 ? "rounded-br-2xl" : ""}`}>
              <img src={g} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <div>
            <div className="flex items-center gap-3 text-sm">
              {park.operator_logo && (
                <div className="bg-white border border-border rounded-lg px-2 py-1 flex items-center gap-1.5" data-testid="detail-operator-logo">
                  <img src={park.operator_logo} alt={park.operator} className="h-5 w-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  <span className="text-xs font-bold">{park.operator}</span>
                </div>
              )}
              <span className="text-ink/70 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{park.region}</span>
              {park.featured && <span className="bg-terracotta text-white text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full">Featured</span>}
            </div>
            <h1 className="font-display font-extrabold text-4xl mt-2" data-testid="park-detail-title">{park.name}</h1>
            <div className="mt-2 flex items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1 font-semibold"><Star className="w-4 h-4 fill-terracotta text-terracotta" />{park.rating}</span>
              <span className="text-ink/60">({park.review_count.toLocaleString()} reviews)</span>
              <span className="text-ink/30">·</span>
              <span className="inline-flex items-center gap-1 text-ink/70"><Waves className="w-4 h-4 text-sky" />{park.beach_distance_km < 1 ? `${Math.round(park.beach_distance_km*1000)}m` : `${park.beach_distance_km}km`} to beach</span>
            </div>
            <p className="mt-5 text-ink/80 leading-relaxed max-w-2xl">{park.description}</p>
            <p className="mt-2 text-sm text-ink/60">{park.address}</p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl mb-3">Facilities</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="park-facilities">
              {park.facilities.map((f) => (
                <div key={f} className="flex items-center gap-2 bg-white border border-border rounded-lg p-3">
                  <FacilityIcon name={f} />
                  <span className="text-sm">{FACILITY_LABELS[f] || f}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl mb-3">Location</h2>
            <ParkMap lat={park.lat} lng={park.lng} name={park.name} />
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl mb-3">Next 90 days · price calendar</h2>
            <div className="bg-white border border-border rounded-xl p-4 overflow-x-auto" data-testid="price-calendar">
              <div className="grid grid-cols-7 gap-1.5 min-w-[640px]">
                {park.price_calendar.map((d) => {
                  const min = Math.min(...park.price_calendar.map((x) => x.per_night));
                  const max = Math.max(...park.price_calendar.map((x) => x.per_night));
                  const ratio = (d.per_night - min) / (max - min || 1);
                  const bg = `hsl(${152 - ratio * 152}, 32%, ${88 - ratio * 25}%)`;
                  const dt = new Date(d.date);
                  return (
                    <div key={d.date} className="price-cal-day rounded-md p-2 text-center" style={{ backgroundColor: bg }}>
                      <div className="text-[10px] text-ink/60">{dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div>
                      <div className="font-semibold text-sm text-ink">£{d.per_night}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-20 bg-white border border-border rounded-2xl p-6" data-testid="booking-card">
            <div className="text-xs uppercase tracking-wider text-ink/50">indicative from</div>
            <div className="font-display text-4xl font-extrabold text-forest leading-none mt-1">
              {formatGBP(park.pricing.per_night)}<span className="text-base font-medium text-ink/60"> /night</span>
            </div>
            <div className="text-sm text-ink/60 mt-1">
              {formatGBP(park.pricing.total)} total for {park.pricing.nights} night{park.pricing.nights>1?"s":""}
            </div>
            <div className="text-xs text-ink/50 mt-1">{park.pricing.arrival} → {park.pricing.departure}</div>

            <div className="mt-4 bg-sand border border-border rounded-lg p-3 text-xs text-ink/70 leading-relaxed" data-testid="availability-disclaimer">
              <strong className="text-ink">Live availability not guaranteed.</strong> Prices shown are indicative based on operator base rates. Tap below to confirm the actual price and that rooms are available for your dates on {park.operator}'s own site.
            </div>

            <a
              href={park.booking_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="btn-primary w-full mt-4"
              data-testid="park-view-deal"
            >
              Check availability on {park.operator} <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={() => toggle(park)}
              disabled={compareDisabled}
              className={`w-full mt-2 text-sm rounded-full px-4 py-2.5 border font-medium ${isCompared ? "bg-forest text-white border-forest" : "bg-white border-border hover:border-forest"} ${compareDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="park-compare-toggle"
            >
              {isCompared ? "Comparing ✓" : "Add to compare"}
            </button>

            <div className="text-[11px] text-ink/50 mt-4 leading-relaxed">
              We may earn an affiliate fee when you book via {park.operator}. This never affects your price.
            </div>
          </div>
        </aside>
      </section>

      {lightboxIdx !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)} data-testid="lightbox">
          <img src={park.gallery[lightboxIdx]} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl" />
        </div>
      )}

      <Footer />
    </div>
  );
}
